<template>
  <PageSplit
    :disabled="$screen === 'm'"
    :padding="[1, 0]"
    :class="[$style.base, { [$style.largeScreen]: $screen === 'l' }]"
  >
    <template #left>
      <Flex
        direction="column"
        :gap="2"
      >
        <Flex direction="column">
          <BaseHeading size="l">
            {{ $t('giftcard_widget.heading') }}
          </BaseHeading>
          <BaseText>{{ $t('giftcard_widget.description') }}</BaseText>
          <BaseSpinner
            v-if="loading"
            inline
          />
          <Flex
            direction="column"
            align="start"
            v-else-if="!hasPaymentGateway"
          >
            <BaseAlert
              color="warning"
              :text="$t('giftcard_widget.payment_alert.description')"
            />
            <BaseButton
              :routerLink="{
                name: 'integrations'
              }"
            >
              {{ $t('giftcard_widget.payment_alert.button_text') }}
            </BaseButton>
          </Flex>
        </Flex>
        <BaseForm @submit="submitForm">
          <BaseAccordion
            v-model="visibleGroups"
            :titles="[
              $t('admin.booking.main_settings'),
              $t('admin.booking.style'),
              $t('admin.booking.sharing')
            ]"
          >
            <template #1>
              <MainSettings />
            </template>
            <template #2>
              <StyleSettings />
            </template>
            <template #3>
              <SharingSettings />
            </template>
          </BaseAccordion>
          <div :class="$style.buttonWrap">
            <BaseButton
              submitForm
              :loading="saving"
              v-test="'giftcard-settings-submit'"
            >
              {{ $t('global.actions.save') }}
            </BaseButton>
          </div>
        </BaseForm>
      </Flex>
    </template>
    <template #right>
      <iframe
        ref="widgetPreviewIframe"
        :key="iframeUpdate"
        :src="widgetPreviewUrl"
        :class="$style.iframe"
        :style="
          $screen === 'l'
            ? undefined
            : {
                height: formData.settings.bookingWidget.height + 'px'
              }
        "
        v-test="'giftcard-settings-preview-iframe'"
      />
    </template>
  </PageSplit>
</template>

<script lang="ts">
export default {
  name: 'VoucherWidgetSettings',
};
</script>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import { flash } from '@/helpers/ui';
import PageSplit from '@/modules/PageSplit.vue';
import { useCompanyStore } from '@/stores/company';
import { useWidgetData } from './form-data';
import MainSettings from './main-settings/index.vue';
import SharingSettings from './sharing-settings/index.vue';
import StyleSettings from './style-settings/index.vue';

const visibleGroups = ref<string[]>([]);
const { t } = useI18n();

const { formData } = useWidgetData();

const { company, updateCompany, isTreatwellUser } = useCompanyStore();

const widgetPreviewUrl = computed(() => {
  let baseUrl = `${config.bookingWidgetUrl}/?widget=voucher&company=${company.publicId}`;
  baseUrl += `&language=${formData.languagePublic.toLowerCase()}`;
  baseUrl += `&color=${formData.settings.bookingWidget.customColor}`;
  if (!formData.settings.bookingWidget.autoWidth) {
    baseUrl += `&width=${formData.settings.bookingWidget.width}`;
  }
  baseUrl += `&position=${formData.settings.bookingWidget.position.toLowerCase()}`;
  baseUrl += `&inline=${formData.settings.bookingWidget.inline}`;

  if (isTreatwellUser) {
    baseUrl += '&treatwell=true';
  }

  return encodeURI(baseUrl);
});

const widgetPreviewIframe = ref<HTMLIFrameElement | null>(null);
const iframeUpdate = ref(0);

const refreshIframe = () => {
  if (widgetPreviewIframe.value) {
    iframeUpdate.value += 1;
  }
};

watch(widgetPreviewUrl, () => {
  refreshIframe();
});

const saving = ref(false);

const submitForm = () => {
  saving.value = true;

  formData.settings.sales.giftcardValues = [
    ...new Set(
      formData.settings.sales.giftcardValues
        .sort((a: number, b: number) => a - b)
        .filter((value: number) => value >= 500 || value <= 100000),
    ),
  ];

  updateCompany(formData)
    .then(() => {
      flash(t('global.flash.settings_updated'));
      refreshIframe();
    })
    .finally(() => {
      saving.value = false;
    });
};

const { result: currentUser, loading } = useQuery(
  gql`
    query getCurrentUser {
      currentUser {
        company {
          paymentGateway
        }
      }
    }
  `,
  null,
  {
    fetchPolicy: 'cache-first',
  },
);

const hasPaymentGateway = computed(
  () => !!currentUser.value?.currentUser.company.paymentGateway,
);
</script>

<style
  lang="scss"
  module
>
.iframe {
  border: 0;
  width: 100%;
  height: 100%;

  .base:not(.largeScreen) & {
    height: 500px;
  }
}

.buttonWrap {
  display: flex;
  justify-content: flex-end;
  margin-top: $spacing * 2;
}
</style>
